import 'moment/locale/cs';
import 'moment/locale/da';
import 'moment/locale/de';
import 'moment/locale/et';
import 'moment/locale/en-gb';
import 'moment/locale/fi';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/lt';
import 'moment/locale/lv';
import 'moment/locale/nl';
import 'moment/locale/nb';
import 'moment/locale/pl';
import 'moment/locale/ru';
import 'moment/locale/sv';
import 'moment/locale/uk';
import 'moment/locale/zh-cn';

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';
import { initReactI18next } from 'react-i18next';

import 'moment-duration-format';

import { getStored } from '~/services/LegacyServices';

export const LANGUAGE_KEY = 'language';
const COMPANY_DEFAULT_LANGUAGE_KEY = 'company_language';
export const DEFAULT_LANG = 'en';

export const supportedLanguages = [
  {
    code: 'en',
    name: 'English',
  },
  {
    code: 'de',
    name: 'Deutsch',
  },
  {
    code: 'da',
    name: 'Dansk',
  },
  {
    code: 'no',
    name: 'Norsk',
  },
  {
    code: 'fi',
    name: 'Suomi',
  },
  {
    code: 'fm',
    name: 'Suomi (Neutraali)',
  },
  {
    code: 'sv',
    name: 'Svenska',
  },
  {
    code: 'ru',
    name: 'Русский',
  },
  {
    code: 'nl',
    name: 'Nederlands',
  },
  {
    code: 'pl',
    name: 'Polski',
  },
  {
    code: 'zh-CN',
    name: '中文',
  },
  {
    code: 'et',
    name: 'Eesti',
  },
  {
    code: 'uk',
    name: 'Українська',
  },
  {
    code: 'lv',
    name: 'Latviešu',
  },
  {
    code: 'lt',
    name: 'Lietuvių',
  },
  {
    code: 'it',
    name: 'Italiano',
  },
  {
    code: 'fr',
    name: 'Français',
  },
  {
    code: 'cs',
    name: 'Čeština',
  },
];

export const getCurrentLanguage = (fallbackLanguage?: string): string => {
  return getStored<string>(LANGUAGE_KEY) || fallbackLanguage || DEFAULT_LANG;
};

export const getCompanyDefaultLanguage = () => {
  return getStored(COMPANY_DEFAULT_LANGUAGE_KEY);
};

// Dates localization
export const getAdapterLocale = (languageCode?: string): string => {
  const language = languageCode ? languageCode : getCurrentLanguage('en-gb');

  const localeConversions = [
    {
      languages: ['en', 'en-us'],
      adapterLocale: 'en-gb',
    },
    {
      languages: ['fm'],
      adapterLocale: 'fi',
    },
  ];

  return (
    localeConversions.find((lc) => lc.languages.includes(language))
      ?.adapterLocale || language
  );
};

export const getMomentDateLibInstance = (languageCode?: string) => {
  moment.locale(getAdapterLocale(languageCode));
  return moment;
};

// Translations
type Translations = {
  [p: string]: string;
};

export const getResources = (
  customOverrides?: Record<string, Translations>,
) => {
  const getShortLangCode = (langCode: string) => langCode.substring(0, 2);

  const getTranslations = (langCode: string) => {
    const filterEmptyTranslations = (translations: Translations) =>
      Object.fromEntries(
        Object.entries(translations).filter(([k, v]) => !(v === '')),
      );

    const mergeTranslationSets = (translations: Translations[]) =>
      Object.assign(
        {},
        ...translations.map((obj) => filterEmptyTranslations(obj)),
      );

    const translationSets = [
      require(`./locales/${langCode}.json`),
      require(`./legacy-locales/${getShortLangCode(langCode)}.json`),
      require(`./error-locales/${getShortLangCode(langCode)}.json`),
    ];

    return mergeTranslationSets(translationSets);
  };

  const base = Object.assign(
    {},
    ...supportedLanguages.map((lc) => {
      return {
        [getShortLangCode(lc.code)]: {
          translation: getTranslations(lc.code),
        },
      };
    }),
  );

  if (customOverrides) {
    Object.entries(customOverrides).forEach(([lang, overrides]) => {
      if (!base[lang]) base[lang] = { translation: {} };
      base[lang].translation = {
        ...base[lang].translation,
        ...overrides,
      };
    });
  }

  return base;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: getResources(),
    lng: getCurrentLanguage('en'),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export const getLanguageLabel = (langCode: string): string => {
  if (langCode) {
    const result = supportedLanguages.find(
      (s: { code: string; name: string }) => s.code === langCode,
    );
    if (result) {
      return result.name;
    }
  }
  return '';
};
